// src/pages/public/PublicStylistProfilePage.tsx
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import PublicSeoPage from './PublicSeoPage';
import { getSeoContext } from '../../seo/seoContext';
import { buildStylistProfileModel } from '../../seo/core/pageModels';
import { isValidSlugSegment } from '../../seo/core/slug';
import { ROUTES } from '../../seo/core/urls';
import publicSeoService from '../../services/publicSeoService';
import type { PublicPageModel, PublicStylist } from '../../seo/types';

/**
 * `/stylists/:seoSlug` — the public, indexable stylist profile.
 *
 * This is a NEW public route, not a move: the authenticated
 * `/client/stylist/:shortCode` view is untouched and still requires a session.
 * The two differ in identifier as well as in access — the public URL uses the
 * human-readable `seoSlug`, never the opaque `shortCode`.
 *
 * The payload comes from `GET /api/public/seo/stylists/:seoSlug`, whose
 * projection excludes email, phone number, street address and everything
 * payment-related, so no private field can reach this page in the first place.
 */
function PublicStylistProfilePage() {
  const ctx = getSeoContext();
  const { seoSlug } = useParams<{ seoSlug: string }>();
  const [stylist, setStylist] = useState<PublicStylist | null>(null);
  const [loading, setLoading] = useState(true);

  const validSlug = isValidSlugSegment(seoSlug);

  useEffect(() => {
    if (!validSlug || !seoSlug) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    publicSeoService.getPublicStylist(seoSlug).then((data) => {
      if (cancelled) return;
      setStylist(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [seoSlug, validSlug]);

  const model = useMemo<PublicPageModel | null>(() => {
    if (!stylist) return null;
    return buildStylistProfileModel(stylist, ctx) as PublicPageModel | null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylist, ctx.environment, ctx.siteUrl]);

  if (!validSlug) return <Navigate to={ROUTES.stylistDirectory()} replace />;

  return <PublicSeoPage model={model} loading={loading} />;
}

export default PublicStylistProfilePage;
